import { useCallback, useEffect, useState } from "react";
import { cn } from "../lib/cn.js";

export function TableResizeHandle({
  onResize,
  className,
}: {
  readonly onResize: (event: React.MouseEvent) => void;
  readonly className?: string;
}): React.JSX.Element {
  return (
    <span
      onMouseDown={onResize}
      onClick={(event) => event.stopPropagation()}
      className={cn(
        "absolute right-0 top-0 bottom-0 w-2.5 cursor-col-resize touch-none select-none z-40 group/resize hover:bg-primary/20 transition-colors",
        className,
      )}
    >
      <span className="absolute right-0 top-0 bottom-0 w-[2px] bg-transparent group-hover/resize:bg-primary transition-colors" />
    </span>
  );
}

export function useTableResize(initialWidths: readonly number[], minWidth = 60) {
  const [colWidths, setColWidths] = useState<number[]>(() => [...initialWidths]);

  useEffect(() => {
    setColWidths([...initialWidths]);
  }, [initialWidths.length]);

  const startResize = useCallback(
    (index: number, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const startX = event.clientX;
      const startWidth = colWidths[index] ?? initialWidths[index] ?? 120;

      const prevCursor = document.body.style.cursor;
      const prevUserSelect = document.body.style.userSelect;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const onMouseMove = (moveEvent: MouseEvent) => {
        const nextWidth = Math.max(startWidth + (moveEvent.clientX - startX), minWidth);
        setColWidths((prev) =>
          prev.map((w, i) => (i === index ? nextWidth : w)),
        );
      };

      const onMouseUp = () => {
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = prevUserSelect;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [colWidths, initialWidths, minWidth],
  );

  return { colWidths, startResize, setColWidths };
}
